import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLazyQuery } from "@stadium/services/apollo-client";
import { useIsAuthGraphQLReady } from "@stadium/services/auth/client";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useOpenOrderContext } from "@contexts/open-order-context";

import { fetchExternalStore } from "@graphql/queries/rest-api/stores/basic-details";
import { isGlobalCountry } from "@stadium/helpers/country/utils";

import { setStoreBalance } from "@slices/store";
import { setCart, emptyCart } from "@slices/cart";

import {
  getIsLockerShop,
  getIsOrganizerShareAdded,
  getStoreIsOrderFetched,
  getStoreExternalId,
} from "@selectors/store";
import { getCountry } from "@selectors/country";

import useRefreshCumulativeCart from "@hooks/cart/use-refresh-cumulative-cart";

import { SWAG_PURCHASED_FROM_US_INTENT } from "@constants/orders/cart";
import { getCartOrderId } from "@selectors/cart";
import { cartRoute } from "@constants/routes";

const useFetchExternalStore = () => {
  const dispatch = useDispatch();

  const { isGuestOrPreviewMode } = usePreviewModeContext();
  const { openOrderId } = useOpenOrderContext();

  const { query, pathname } = useRouter();

  const isCartPage = pathname.split("/").pop() === cartRoute.split("/").pop();

  const {
    fetchCumulativeCart,
    fetchAvailability,
    refreshCart,
    fetchCart,
    loading: loadingCartData,
  } = useRefreshCumulativeCart();
  const isLockerShop = useSelector(getIsLockerShop);
  const isOrganizerShareAdded = useSelector(getIsOrganizerShareAdded);
  const country = useSelector(getCountry);
  const orderId = useSelector(getCartOrderId);
  const isGlobal = isGlobalCountry(country);
  const isStoreOrderFetched = useSelector(getStoreIsOrderFetched);
  const externalId = useSelector(getStoreExternalId);

  const storeId = isGuestOrPreviewMode || query.token ? null : externalId;

  const programIntent = isLockerShop ? SWAG_PURCHASED_FROM_US_INTENT : undefined;
  const [fetchStore, { data, loading }] = useLazyQuery(fetchExternalStore, {
    variables: { storeId, programIntent },
    skip: !storeId,
    fetchPolicy: "cache-and-network",
  });

  const isAuthGraphQLReady = useIsAuthGraphQLReady();

  const fetchCartDataForOpenOrder = () => {
    if (openOrderId) {
      isCartPage ? fetchAvailability(openOrderId) : fetchCumulativeCart(openOrderId);
    }
  };

  const updateStoreData = () => {
    fetchStore().then(({ data }) => {
      if (data?.externalStore) {
        const cartPayload = data.externalStore.order;
        const storePayload = {
          balance: data.externalStore.availableStoreSpecificPoints,
          initBalance: data.externalStore.availableStoreSpecificPoints,
          isStoreOrderFetched: true,
        };

        dispatch(setStoreBalance(storePayload));
        if (!openOrderId && cartPayload && !loadingCartData) {
          dispatch(setCart(cartPayload));
          if (isCartPage) {
            fetchAvailability(cartPayload.id);
          } else if (isGlobal) {
            fetchCart(cartPayload.id);
          } else if (isLockerShop) {
            refreshCart(cartPayload.id);
          } else {
            fetchCumulativeCart(cartPayload.id);
          }
        } else {
          fetchCartDataForOpenOrder();
        }

        //to set orderFetching to false for case when order is
        //null and refreshCumulativeCart is not getting called
        if (!cartPayload) {
          dispatch(emptyCart());
        }
      }
    });
  };

  useEffect(() => {
    if (!isAuthGraphQLReady) {
      return;
    }
    if (storeId && !loading && !orderId && !isStoreOrderFetched) {
      if (isOrganizerShareAdded) {
        fetchCartDataForOpenOrder();
      } else {
        updateStoreData();
      }
    }
  }, [
    dispatch,
    fetchStore,
    storeId,
    openOrderId,
    isOrganizerShareAdded,
    orderId,
    isStoreOrderFetched,
    isCartPage,
    isAuthGraphQLReady,
  ]);

  return {
    updateStoreData,
    data,
    loading,
  };
};

export default useFetchExternalStore;
